import { FormStore } from '@roc/feature-app-core';
import { GlobalStore } from '@roc/feature-app-core';
import { AppraisalsService } from '@roc/feature-appraisals';
import { OneToolService } from '../../services/oneToolService';
import {
  DescribedRennovation,
  floridaCountiesRestriction,
  generateUUID,
  GENERIC_ERROR_MESSAGE,
  LoanSubType,
  NEW_CONSTRUCTION,
  notLicensedinState,
  parseAddress,
  propertyCitiesRestriction,
  PropertyOwnership,
  PURCHASE,
  REFINANCE,
  statePropertyTypeRestriction,
  getStateLicenseMatrixMessage,
} from '@roc/feature-utils';

import { isRestrictedCounty } from '../../utils/utils';

import { action, computed, flow, makeObservable, observable, override } from 'mobx';
import { getUnitsFromPropertyType, mapPropertyFields } from '../../utils/utils';
import { MSAService } from 'libs/feature-loans/src/loanSubmission/services/msaService';
import { isNil } from '@roc/ui/utils';

const addressEquals = (addressA, addressB) => {
  return (
    addressA.streetNumber == addressB.streetNumber &&
    addressA.streetName == addressB.streetName &&
    addressA.city == addressB.city &&
    addressA.state == addressB.state &&
    addressA.zipCode == addressB.zipCode &&
    (addressA.aptNumber ?? '') == (addressB.aptNumber ?? '')
  );
};

const RenoDictionary = {
  "NO": DescribedRennovation.NO,
  "HORIZONTAL": DescribedRennovation.HORIZONTAL,
  "VERTICAL": DescribedRennovation.VERTICAL,
  "VERTICAL_AND_HORIZONTAL": DescribedRennovation.VERTICAL_AND_HORIZONTAL,
  [DescribedRennovation.CONDO_CONVERSION]: DescribedRennovation.CONDO_CONVERSION,
  [DescribedRennovation.UNIT_COUNT]: DescribedRennovation.UNIT_COUNT,
  [DescribedRennovation.ADU_CONVERSION]: DescribedRennovation.ADU_CONVERSION,
  [DescribedRennovation.FIRE_DAMAGE]: DescribedRennovation.FIRE_DAMAGE,
  [DescribedRennovation.CONVERSION_EXISTING_SPACE]: DescribedRennovation.CONVERSION_EXISTING_SPACE,
};

export class PropertyFormStore extends FormStore {
  private globalStore: GlobalStore;
  private appraisalService: AppraisalsService;
  private oneToolService: OneToolService;
  public loanSubType: string;
  public isPropertyMSARural: boolean;
  private msaService = new MSAService();

  key = generateUUID();
  isEditing = true;
  licenseRequired = false;
  stateLicenseMessage: string = null;
  private stateLicenseMatrix: any[] = null;
  private stateLicenseMatrixPromise: Promise<any> = null;

  constructor(form, globalStore, loanSubType) {
    super(form, globalStore);
    this.globalStore = globalStore;
    this.loanSubType = loanSubType;
    this.appraisalService = new AppraisalsService();
    this.oneToolService = new OneToolService();
    this.prepopulateNewProperty();

    makeObservable(this, {
      key: observable,
      isEditing: observable,
      licenseRequired: observable,
      stateLicenseMessage: observable,
      findAppraisals: flow,
      getTotalExpenses: action,
      reset: override,
      checkIfPropertyIsRural: flow,
      checkLicenseRequired: flow,
      isValidLoanSubtypeForCityRestrictions: computed,
      isPropertyMSARural: observable
    });
  }

  *checkLicenseRequired(state: string) {
    try {
      if (!state) {
        this.licenseRequired = false;
        this.stateLicenseMessage = null;
        return;
      }
      if (this.stateLicenseMatrix === null) {
        // Reuse a single in-flight request so concurrent callers don't each hit getAll.
        if (!this.stateLicenseMatrixPromise) {
          this.stateLicenseMatrixPromise = this.oneToolService.getAllStateLicenseMatrix();
        }
        const response = yield this.stateLicenseMatrixPromise;
        this.stateLicenseMatrix = response?.data?.data ?? [];
      }
      const entry = this.stateLicenseMatrix.find(m => m.state === state);
      this.licenseRequired = entry?.companyLicenseRequired || entry?.originatorLicenseRequired;
      this.stateLicenseMessage = getStateLicenseMatrixMessage(entry, this.globalStore.lenderInfo?.isInternal);
    } catch {
      this.licenseRequired = false;
      this.stateLicenseMessage = null;
    }
  }

  reset() {
    super.reset();
    this.isEditing = true;
    this.form.meta.editMode = true;
    this.form.meta.key = this.key;
  }

  prepopulateNewProperty() {
    this.form.meta.key = generateUUID();
    this.form.meta.editMode = true;
  }
  setEditMode(value) {
    this.form.meta.editMode = value;
    this.isEditing = value;
  }
  saveAddressField(address: string, addressDetails, geometryDetails) {
    const parsedAddress = parseAddress(
      address,
      addressDetails,
      geometryDetails
    );

    const streetAddress =
      parsedAddress.street_number === null
        ? parsedAddress.street
        : parsedAddress.street_number + ' ' + parsedAddress.street;
    this.onFieldChange('address', streetAddress);
    this.onFieldChange('streetNumber', parsedAddress.street_number);
    this.onFieldChange('streetName', parsedAddress.street);
    this.onFieldChange('city', parsedAddress.city);
    this.onFieldChange('state', parsedAddress.state);
    this.onFieldChange('county', parsedAddress.county);
    this.onFieldChange('zipCode', parsedAddress.zip);
    this.onFieldChange('latitude', parsedAddress.latitude);
    this.onFieldChange('longitude', parsedAddress.longitude);
    this.onFieldChange('aptNumber', parsedAddress.aptNumber);
    this.findAppraisals(address);
  }

  *findAppraisals(address) {
    return;
    try {
      const values = this.getFormValues();
      const propertyAddress = address.replace(/, EUA$/i, '');
      const response = yield this.appraisalService.getStandaloneAppraisals(
        1,
        20,
        null,
        null,
        { propertyAddress },
        {}
      );
      const appraisals = response.data.data.rows;
      const appraisalAvailable = appraisals
        .map(row => JSON.parse(row.appraisalData.propertyAppraisalOrderJson))
        .find(appraisalData => addressEquals(appraisalData.property, values));

      this.setPropertyAppraisalValues(appraisalAvailable);
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  setPropertyAppraisalValues(appraisalAvailable) {
    try {
      if (!isNil(appraisalAvailable) && this.loanSubType != LoanSubType.RENTAL_PORTFOLIO) {
        this.onFieldChange('isAppraisalAvailable', true);
        this.onFieldChange('propertyId', appraisalAvailable?.property?.propertyId);
        this.onFieldChange('propertyType', appraisalAvailable?.property?.propertyType);
        this.onFieldChange('loanPurpose', appraisalAvailable?.propertyOwnership == PropertyOwnership.OWNED_BY_BORROWER ? REFINANCE : PURCHASE);
        this.onFieldChange('purchaseDate', appraisalAvailable?.purchaseDate);
        this.onFieldChange('propertySourcing', appraisalAvailable?.propertySourcing);
        this.onFieldChange('payoffAmount', appraisalAvailable?.payoffAmount);
        this.onFieldChange('purchasePrice', appraisalAvailable?.purchasePrice);
        this.onFieldChange('currentMarketValue', appraisalAvailable?.asIsValue);
        this.onFieldChange('constructionRenoSpendToDate', appraisalAvailable?.renovationSpentToDate);
        if (this.loanSubType == LoanSubType.FIX_AND_FLIP || this.loanSubType == LoanSubType.GROUND_UP) {
          this.onFieldChange('renovationBudget', appraisalAvailable?.renovationBudget);
          this.onFieldChange('marketValueAfterCompletion', appraisalAvailable?.afterRepairValue);
        }
        this.onFieldChange('wholesalerPropertyContractAmount', appraisalAvailable?.wholesaleAmount);
        this.onFieldChange('propertySourcingExplanation', appraisalAvailable?.property?.propertySourcingExplanation);
        this.onFieldChange('exitStrategy', appraisalAvailable?.exitStrategy);
      }
    } catch (e) {
      console.log(e);
    }
  }

  public *checkIfPropertyIsRural(address: string) {
    const response = yield this.msaService.checkIfPropertyIsRural(address);
    this.isPropertyMSARural = response.data.data;
  }

  /**
   * Validates the property fields and cleans up unused fields (fields hidden because of other fields values)
   */
  validateAndCleanupProperty(message?: string) {
    this.cleanupUnusedFields();
    const state = this.form.fields.state.value;
    const city = this.form.fields.city.value;
    const propertyType = this.form.fields.propertyType.value;
    const county = this.form.fields.county.value?.toLowerCase();
    if (notLicensedinState.states.includes(state)) {
      this.globalStore.notificationStore.showWarningNotification({
        message: notLicensedinState.message,
      });
      return false;
    }
    if (statePropertyTypeRestriction.states.includes(state) && statePropertyTypeRestriction.propertyTypes.includes(propertyType)) {
      this.globalStore.notificationStore.showWarningNotification({
        message: statePropertyTypeRestriction.message,
      });
      return false;
    }

    if (this.isValidLoanSubtypeForCityRestrictions && propertyCitiesRestriction.cities.includes(city?.trim().toLowerCase())) {
      this.globalStore.notificationStore.showWarningNotification({
        message: propertyCitiesRestriction.message,
      });
      return false;
    }
    if (this.isValidLoanSubtypeForFloridaCountyRestrictions && isRestrictedCounty(county, floridaCountiesRestriction.counties, state, floridaCountiesRestriction.states)) {
      this.globalStore.notificationStore.showWarningNotification({
        message: floridaCountiesRestriction.message,
      });
      return false;
    }

    if (message) {
      this.runFormValidationWithMessage(message);
    } else {
      this.runFormValidation();
    }

    return this.form.meta.isValid;
  }

  get isValidLoanSubtypeForCityRestrictions() {
    return this.loanSubType === LoanSubType.RENTAL_PORTFOLIO || this.loanSubType === LoanSubType.SINGLE_PROPERTY || this.loanSubType === LoanSubType.STABILIZED_BRIDGE
  }

  get isValidLoanSubtypeForFloridaCountyRestrictions() {
    return this.loanSubType === LoanSubType.RENTAL_PORTFOLIO || this.loanSubType === LoanSubType.SINGLE_PROPERTY || this.loanSubType === LoanSubType.STABILIZED_BRIDGE || this.loanSubType === LoanSubType.FIX_AND_FLIP || this.loanSubType === LoanSubType.GROUND_UP
  }

  loadProperty(property) {
    this.loadForm({
      ...property,
      city: property?.city || '',
      state: property?.state || '',
      zipCode: property?.zipCode || '',
      describeRenovation: this.getDescribedRenovation(property),
      cashOut: property.seekingCashOut
    });
    this.key = property.uuid;
    this.getTotalExpenses()
  }

  getDescribedRenovation(property: any) {
    if (this.loanSubType === LoanSubType.GROUND_UP) {
      return property?.describeRenovation || NEW_CONSTRUCTION;
    }
    else {
      const describedReno = property?.describeRenovation && Object.values(RenoDictionary).includes(property.describeRenovation) ? property.describeRenovation : RenoDictionary[property?.squareFootageExpansion];
      return describedReno || DescribedRennovation.NO;
    }
  }

  getProperty() {
    const property = mapPropertyFields(this.getFormValues(), this.loanSubType);
    return {
      ...property,
      uuid: this.key,
    };
  }
  /**
   * Cleans up unused fields (fields hidden because of other fields values)
   */
  cleanupUnusedFields() {
    const property = this.getProperty();
    this.reset();
    this.loadForm(property);
  }

  validateProperty(prefight?: boolean) {
    this.onFieldChange('isPreflight', Boolean(prefight));
    this.runFormValidationWithMessage();
    return this.form.meta.isValid;
  }

  onFieldChange(field, value) {
    super.onFieldChange(field, value);
    if (field === 'state') {
      this.checkLicenseRequired(value);
    }
    if (field === 'propertyType' && this.form.fields.units !== undefined) {
      super.onFieldChange('units', getUnitsFromPropertyType(value));
    }
    if (field == 'loanPurpose') {
      if (value == REFINANCE) {
        super.onFieldChange('propertySourcing', '');
        super.onFieldChange('sellerCredit', '');
        super.onFieldChange('sellerCreditAmount', '');
        super.onFieldChange('borrowerKnowOwnerOfRecord', '');
        super.onFieldChange('armsLengthComment', '');
        super.onFieldChange('wholesalerPropertyContractAmount', '');
        super.onFieldChange('propertySourcingExplanation', '');
        if (this.loanSubType == LoanSubType.GROUND_UP) {
          super.onFieldChange('subdividedIntoLots', '');
          super.onFieldChange('numberOfLots', '');
        }
        else if (this.loanSubType == LoanSubType.STABILIZED_BRIDGE) {
          super.onFieldChange('squareFootageExpansion', '');
        }
      }
      if (value == PURCHASE) {
        super.onFieldChange('purchaseDate', null);
        super.onFieldChange('outstandingLoanOnTheProperty', '');
        super.onFieldChange('payoffAmount', '');
        super.onFieldChange('loanEverBeenInDefault', '');
        super.onFieldChange('constructionRenoSpendToDate', '');
        if (this.loanSubType == LoanSubType.FIX_AND_FLIP) {
          super.onFieldChange('currentMarketValue', '');
        }
        else if (this.loanSubType == LoanSubType.GROUND_UP) {
          super.onFieldChange('currentMarketValue', '');
        }
        else if (this.loanSubType == LoanSubType.STABILIZED_BRIDGE) {
          super.onFieldChange('holdingCosts', '');
        }
      }
    }
  }

  getTotalExpenses = () => {
    if (this.loanSubType === LoanSubType.SINGLE_PROPERTY) {
      const {
        annualTaxes,
        annualInsurance,
        annualHOAProvidedAtOrigination,
      } = this.form.fields;
      const taxes = isNaN(Number(annualTaxes.value))
        ? 0
        : Number(annualTaxes.value);
      const insurance = isNaN(Number(annualInsurance.value))
        ? 0
        : Number(annualInsurance.value);
      const hoa = isNaN(Number(annualHOAProvidedAtOrigination.value)) ? 0 : Number(annualHOAProvidedAtOrigination.value);

      const totalAnnualExpenses =
        taxes +
        insurance +
        hoa

      this.onFieldChange('totalAnnualExpenses', totalAnnualExpenses);
    }
  };
}
